import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { HeroUIProvider, ToastProvider } from '@heroui/react';
import { I18nProvider } from '@react-aria/i18n';
import * as Sentry from '@sentry/react';

import { Crash } from '@/components/layout/Crash';
import { NoMatch } from '@/components/layout/NoMatch';
import { ActionCableProvider } from '@/context/ActionCableContext';
import { DismissProvider } from '@/context/DismissableContext';
import useLocale from '@/hooks/useLocale';
import useSegment from '@/hooks/useSegment';
import { AppRouter } from '@/navigation/Router';
import { setRoute, setTestEnv } from '@/patient-app-common/slices/appSlice';
import { useAppSelector } from '@/redux/hooks';

import { DOMAINS } from './domains';
import './dayjs-plugins';
import IdleTimer from './IdleTimer';

function App() {
  const { hostname } = window.location;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { i18n } = useTranslation();
  const { trackUser } = useSegment();

  const { theme } = useTheme();
  const { patientId } = useAppSelector(({ auth }) => auth);
  const { testEnv, route: persistedRoute } = useAppSelector(({ app }) => app);

  useLocale({ skip: !patientId });

  const domain = useMemo(() => {
    let validURL = false;
    let route;

    if (DOMAINS.CA.includes(hostname) || DOMAINS.LOCAL.includes(hostname)) {
      route = import.meta.env.VITE_API_CA;
      validURL = true;
    }

    if (DOMAINS.LOCAL.includes(hostname)) {
      route = import.meta.env.VITE_API_LOCAL;
      validURL = true;
    }

    if (DOMAINS.SANDBOX.includes(hostname)) {
      route = import.meta.env.VITE_API_SANDBOX;
      validURL = true;
    }

    if (DOMAINS.US.includes(hostname)) {
      route = import.meta.env.VITE_API_US;
      validURL = true;
    }

    if (
      hostname.includes('azurestaticapps') ||
      hostname.includes('seamlessmd.workers.dev') ||
      hostname.includes('northamerica-northeast1.run.app')
    ) {
      route = import.meta.env.VITE_API_CA;
      validURL = true;
    }

    return { route, validURL };
  }, [hostname]);

  useEffect(() => {
    if (domain.route !== undefined && (!testEnv || !persistedRoute)) {
      dispatch(setRoute(domain.route));
    }
  }, [domain, testEnv, persistedRoute]);

  useEffect(() => {
    if (
      hostname.includes('app-gcp-web') ||
      hostname.includes('azurestaticapps')
    ) {
      dispatch(setTestEnv(true));
    }
  }, [hostname]);

  useEffect(() => {
    if (patientId) {
      Sentry.setUser({ patientId });
      trackUser(patientId);
    }
  }, [patientId]);

  if (!domain.validURL) {
    return <NoMatch />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<Crash />}>
      <HeroUIProvider navigate={navigate}>
        <ToastProvider />
        <NextThemesProvider
          attribute="class"
          defaultTheme={theme}
          enableSystem={false}
        >
          <I18nProvider locale={i18n.language}>
            <ActionCableProvider>
              <DismissProvider>
                <>
                  <IdleTimer />
                  <AppRouter />
                </>
              </DismissProvider>
            </ActionCableProvider>
          </I18nProvider>
        </NextThemesProvider>
      </HeroUIProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
